// packageManagement.js - User-facing package management functions
import { 
  doc, 
  getDoc, 
  updateDoc, 
  serverTimestamp,
  collection,
  query,
  where,
  getDocs
} from 'firebase/firestore';
import { db } from '../firebase/config';

/**
 * PACKAGE VALIDATION - Check if user can create new team
 * @param {string} userEmail - User email
 * @returns {Promise<Object>} { canScan: boolean, message: string, scansLeft: number }
 */
export const validateScanAvailability = async (userEmail) => {
  try {
    console.log('🔍 Validating scan availability for:', userEmail);
    
    const userDoc = await getDoc(doc(db, 'users', userEmail));
    
    if (!userDoc.exists()) {
      return {
        canScan: false,
        message: 'Gebruiker niet gevonden',
        scansLeft: 0
      };
    }
    
    const userData = userDoc.data();
    
    // Check if user has package
    if (!userData.packageScansTotal) {
      return {
        canScan: false,
        message: 'Geen actieve scans(s). Neem contact op met diederik@tractum.nl.',
        scansLeft: 0
      };
    }
    
    // Check package expiry
    const now = new Date();
    const expiryDate = userData.packageExpiresAt?.toDate();
    
    if (expiryDate && expiryDate < now) {
      return {
        canScan: false,
        message: 'Scans(s) verlopen. Neem contact op met diederik@tractum.nl.',
        scansLeft: 0
      };
    }
    
    // Check scan limit
    const scansUsed = userData.packageScansUsed || 0;
    const scansTotal = userData.packageScansTotal || 0;
    const scansLeft = scansTotal - scansUsed;
    
    if (scansLeft <= 0) {
      return {
        canScan: false,
        message: 'Alle scans gebruikt. Koop extra scans via diederik@tractum.nl.',
        scansLeft: 0
      };
    }
    
    console.log('✅ Scan validation passed:', { scansLeft });
    
    return {
      canScan: true,
      message: `Je hebt nog ${scansLeft} scan(s) beschikbaar`,
      scansLeft
    };
    
  } catch (error) {
    console.error('❌ Error validating scan availability:', error);
    return {
      canScan: false,
      message: 'Fout bij validatie. Probeer opnieuw.',
      scansLeft: 0
    };
  }
};

/**
 * INCREMENT SCAN USAGE - Use one scan when team is created
 * @param {string} userEmail - User email
 * @returns {Promise<Object>} { success: boolean, newCount: number, message: string }
 */
export const incrementScanUsage = async (userEmail) => {
  try {
    console.log('📊 Incrementing scan usage for:', userEmail);
    
    // First validate
    const validation = await validateScanAvailability(userEmail);
    if (!validation.canScan) {
      return {
        success: false,
        newCount: 0,
        message: validation.message
      };
    }
    
    const userRef = doc(db, 'users', userEmail);
    const userDoc = await getDoc(userRef);
    const userData = userDoc.data();
    
    const currentUsed = userData.packageScansUsed || 0;
    const newCount = currentUsed + 1;
    
    await updateDoc(userRef, {
      packageScansUsed: newCount,
      lastScanUsed: serverTimestamp()
    });
    
    const scansLeft = (userData.packageScansTotal || 0) - newCount;
    
    console.log('✅ Scan usage incremented:', { newCount, scansLeft });
    
    return {
      success: true,
      newCount,
      message: `Scan gebruikt. Je hebt nog ${scansLeft} scan(s) over.`
    };
    
  } catch (error) {
    console.error('❌ Error incrementing scan usage:', error);
    return {
      success: false,
      newCount: 0,
      message: 'Fout bij scan registratie. Probeer opnieuw.'
    };
  }
};

/**
 * GET PACKAGE STATUS - Get current package info for user dashboard
 * @param {string} userEmail - User email
 * @returns {Promise<Object>} { scansUsed, scansTotal, expiresAt, isExpired, daysLeft }
 */
export const getPackageStatus = async (userEmail) => {
  try {
    console.log('📋 Getting package status for:', userEmail);
    
    const userDoc = await getDoc(doc(db, 'users', userEmail));
    
    if (!userDoc.exists()) {
      return {
        scansUsed: 0,
        scansTotal: 0,
        expiresAt: null,
        isExpired: true,
        daysLeft: 0,
        hasPackage: false,
        message: 'Geen scan(s) gevonden'
      };
    }
    
    const userData = userDoc.data();
    
    // No package data
    if (!userData.packageScansTotal) {
      return {
        scansUsed: 0,
        scansTotal: 0,
        expiresAt: null,
        isExpired: true,
        daysLeft: 0,
        hasPackage: false,
        message: 'Geen actieve scan'
      };
    }
    
    const scansUsed = userData.packageScansUsed || 0;
    const scansTotal = userData.packageScansTotal || 0;
    const expiresAt = userData.packageExpiresAt?.toDate();
    
    // Calculate expiry info
    let isExpired = false;
    let daysLeft = 0;
    
    if (expiresAt) {
      const now = new Date();
      const timeDiff = expiresAt.getTime() - now.getTime();
      daysLeft = Math.ceil(timeDiff / (1000 * 3600 * 24));
      isExpired = daysLeft <= 0;
    }
    
    const packageInfo = {
      scansUsed,
      scansTotal,
      scansLeft: scansTotal - scansUsed,
      expiresAt,
      isExpired,
      daysLeft: Math.max(0, daysLeft),
      hasPackage: true,
      packageType: userData.packageType || 'unknown',
      activatedBy: userData.packageActivatedBy,
      activatedAt: userData.packageActivatedAt
    };
    
    // Status message
    if (isExpired) {
      packageInfo.message = 'Scan(s) verlopen';
    } else if (packageInfo.scansLeft <= 0) {
      packageInfo.message = 'Alle scans gebruikt';
    } else if (packageInfo.scansLeft === 1) {
      packageInfo.message = 'Laatste scan beschikbaar';
    } else {
      packageInfo.message = `${packageInfo.scansLeft} scans beschikbaar`;
    }
    
    console.log('✅ Package status retrieved:', packageInfo);
    return packageInfo;
    
  } catch (error) {
    console.error('❌ Error getting package status:', error);
    return {
      scansUsed: 0,
      scansTotal: 0,
      expiresAt: null,
      isExpired: true,
      daysLeft: 0,
      hasPackage: false,
      message: 'Fout bij ophalen scan status'
    };
  }
};

/**
 * CHECK PACKAGE EXPIRY - Simple expiry check
 * @param {string} userEmail - User email
 * @returns {Promise<Object>} { isExpired: boolean, daysLeft: number }
 */
export const checkPackageExpiry = async (userEmail) => {
  try {
    const userDoc = await getDoc(doc(db, 'users', userEmail));
    
    if (!userDoc.exists() || !userDoc.data().packageExpiresAt) {
      return {
        isExpired: true,
        daysLeft: 0
      };
    }
    
    const expiresAt = userDoc.data().packageExpiresAt.toDate();
    const now = new Date();
    const timeDiff = expiresAt.getTime() - now.getTime();
    const daysLeft = Math.ceil(timeDiff / (1000 * 3600 * 24));
    
    return {
      isExpired: daysLeft <= 0,
      daysLeft: Math.max(0, daysLeft)
    };
    
  } catch (error) {
    console.error('❌ Error checking package expiry:', error);
    return {
      isExpired: true,
      daysLeft: 0
    };
  }
};

/**
 * CHECK TEAM COMPARISON ACCESS - Security validation for team comparison
 * @param {string} userEmail - User requesting comparison
 * @param {Array} teamIds - Team IDs to compare
 * @returns {Promise<Object>} Access validation result
 */
export const validateTeamComparisonAccess = async (userEmail, teamIds) => {
  try {
    console.log('🔒 Validating team comparison access for:', userEmail);
    
    // Check if user has active package
    const activePackage = await getPackageStatus(userEmail);
    if (!activePackage.hasPackage) {
      return {
        hasAccess: false,
        reason: 'No active package found',
        allowedTeams: []
      };
    }
    
    // Get user's teams to verify ownership
    const teamsQuery = query(
      collection(db, 'teams'),
      where('createdByEmail', '==', userEmail)
    );
    
    const teamsSnapshot = await getDocs(teamsQuery);
    const userTeamIds = teamsSnapshot.docs.map(doc => doc.id);
    
    // Filter to only teams user owns
    const allowedTeams = teamIds.filter(teamId => userTeamIds.includes(teamId));
    
    return {
      hasAccess: allowedTeams.length > 0,
      reason: allowedTeams.length > 0 ? 'Access granted' : 'No owned teams in comparison',
      allowedTeams,
      packageInfo: {
        type: activePackage.packageType,
        scansRemaining: activePackage.scansLeft
      }
    };
    
  } catch (error) {
    console.error('❌ Error validating team comparison access:', error);
    return {
      hasAccess: false,
      reason: 'Validation error: ' + error.message,
      allowedTeams: []
    };
  }
};